<template>
  <SFDialog :title="localize('title')" size="sm">
    <div class="flex flex-col gap-4">
      <SFSelect ref="tool-ref" v-model="tool" :label="localize('field.tool')" :options="toolOptions" required />
      <SFSelect ref="type-ref" v-model="type" :label="localize('field.type')" :options="typeOptions" required />
      <SFInput ref="email-ref" v-model="email" :label="localize('field.email')" :placeholder="localize('field.email')" type="email" maxlength="50" />
      <SFTextarea ref="description-ref" v-model="description" :label="localize('field.description')" :placeholder="localize('field.description')" required rows="7" maxlength="1000" />
    </div>

    <template #buttons>
      <SFButton block :disabled="isSubmitting" @click="emit('close')">
        {{ localize.global('dialog.shared.cancel') }}
      </SFButton>
      <SFButton variant="primary" block :disabled="isSubmitting ? 'loading' : !isValid" @click="submit">
        {{ localize.global('dialog.shared.submit') }}
      </SFButton>
    </template>
  </SFDialog>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import SFButton from '@library/SFButton.vue'
import SFDialog from '@library/SFDialog.vue'
import SFInput from '@library/SFInput.vue'
import SFSelect from '@library/SFSelect.vue'
import SFTextarea from '@library/SFTextarea.vue'
import { type SelectOption } from '@utils/components'
import { useLocalize } from '@utils/localization'
import { useSuccessToast } from '@utils/toasts'
import { useSubmit } from '@utils/utils'
import { useComponentValidation } from '@utils/validations'
import { SiteAPI } from '~/core/api'
import { FEEDBACK_TOOLS, type FeedbackTool } from '~/dialogs/feedback'

defineOptions({
  name: 'FeedbackDialog'
})

const props = withDefaults(
  defineProps<{
    /**
     * Tool the feedback is about, `general` for none
     */
    tool?: FeedbackTool | 'general'
  }>(),
  {
    tool: 'general'
  }
)

const emit = defineEmits<{
  close: []
}>()

const localize = useLocalize('dialog.report')

const tool = ref(props.tool)
const type = ref('issue')
const email = ref('')
const description = ref('')

const toolOptions = computed<SelectOption<FeedbackTool | 'general'>[]>(() => [{ value: 'general', label: '-' }, ...FEEDBACK_TOOLS.map((value) => ({ value, label: localize.global(`index.${value}.title`) }))])

const typeOptions = computed<SelectOption[]>(() => ['issue', 'suggestion'].map((value) => ({ value, label: localize(`type.${value}`) })))

const isValid = useComponentValidation(useTemplateRef('tool-ref'), useTemplateRef('type-ref'), useTemplateRef('email-ref'), useTemplateRef('description-ref'))

const { submit, isSubmitting } = useSubmit(
  async () => {
    await SiteAPI.post('feedback', {
      tool: tool.value,
      type: type.value,
      email: email.value,
      description: description.value
    })

    emit('close')

    useSuccessToast(localize('toast.success.title'), localize('toast.success.message'))
  },
  () => ({ title: localize('toast.error.title'), message: localize('toast.error.message') })
)
</script>
